import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type MessageRow, type SourceSummary } from '../lib/api'
import { fmtDateTime, fmtFull, humanReason, maskMsisdn } from '../lib/format'
import { Card, Empty, Loading, Modal, StatusPill } from '../components/ui'

const STATUSES = ['Pending', 'Parked', 'Dispatched', 'Submitted', 'Delivered', 'Undelivered', 'Failed', 'Expired', 'Cancelled']

export default function Messages() {
  const [status, setStatus] = useState('')
  const [to, setTo] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.get<SourceSummary[]>('/api/admin/sources') })

  const messages = useQuery({
    queryKey: ['messages', status, to, sourceId, q],
    queryFn: () =>
      api.get<MessageRow[]>('/api/admin/messages', {
        status: status || undefined,
        to: to || undefined,
        sourceId: sourceId || undefined,
        q: q || undefined,
        limit: 100,
      }),
    refetchInterval: 8_000,
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Messages</h1>
          <div className="page-sub">
            Every delivery, newest first. Open one to see exactly why it did or did not arrive.
          </div>
        </div>
      </div>

      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search message text…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input
          type="search"
          placeholder="Recipient number"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          style={{ width: 170 }}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
          <option value="">Any status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} style={{ width: 170 }}>
          <option value="">Any source</option>
          {sources.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <Card flush>
        {messages.isLoading ? (
          <Loading />
        ) : messages.data?.length === 0 ? (
          <Empty title="Nothing matches" hint="Try clearing the filters." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>Message</th>
                  <th>Source</th>
                  <th>Device</th>
                  <th>Status</th>
                  <th className="num">Tries</th>
                </tr>
              </thead>
              <tbody>
                {messages.data?.map((m) => (
                  <tr key={m.deliveryId} className="clickable" onClick={() => setOpen(m.messageId)}>
                    <td className="faint small" title={fmtFull(m.createdAt)}>
                      {fmtDateTime(m.createdAt)}
                    </td>
                    <td className="mono">{maskMsisdn(m.msisdn)}</td>
                    <td className="muted" style={{ maxWidth: 340 }}>
                      {m.body ? (m.body.length > 62 ? `${m.body.slice(0, 62)}…` : m.body) : '—'}
                    </td>
                    <td className="small">{m.sourceName ?? '—'}</td>
                    <td className="small">{m.clientName ?? '—'}</td>
                    <td>
                      <StatusPill status={m.status} />
                      {m.reasonCode && <div className="faint small">{humanReason(m.reasonCode)}</div>}
                    </td>
                    <td className="num">{m.attemptCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && <MessageDetail id={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

interface DetailAttempt {
  attemptNo: number
  routingReason: string
  startedAt: string
  endedAt: string | null
  outcome: string | null
  reasonCode: string | null
  reasonDetail: string | null
  modemError: string | null
  clientName: string | null
}

interface DetailDelivery {
  id: string
  msisdn: string
  status: string
  reasonCode: string | null
  reasonDetail: string | null
  attemptCount: number
  createdAt: string
  dispatchedAt: string | null
  submittedAt: string | null
  deliveredAt: string | null
  failedAt: string | null
  clientName: string | null
  verdict: string | null
  parts: { seq: number; tpduLength: number; status: string; messageRef: number | null }[]
  attempts: DetailAttempt[]
}

interface MessageDetailData {
  id: string
  body: string
  correlationId: string | null
  traceId: string
  encoding: string
  partCount: number
  status: string
  priority: string
  scheduledAt: string | null
  expiresAt: string
  createdAt: string
  completedAt: string | null
  sourceName: string | null
  deliveries: DetailDelivery[]
}

function MessageDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['message', id],
    queryFn: () => api.get<MessageDetailData>(`/api/admin/messages/${id}`),
  })

  return (
    <Modal title="Message" onClose={onClose} wide>
      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack" style={{ gap: 18 }}>
          <div>
            <div
              style={{
                padding: '12px 14px',
                background: 'rgb(var(--bg))',
                border: '1px solid rgb(var(--line))',
                borderRadius: 'var(--radius-sm)',
                lineHeight: 1.55,
              }}
            >
              {data.body}
            </div>
          </div>

          <dl className="kv">
            <dt>Source</dt>
            <dd>{data.sourceName ?? '—'}</dd>
            <dt>Status</dt>
            <dd>
              <StatusPill status={data.status} />
            </dd>
            <dt>Encoding</dt>
            <dd>
              {data.encoding.toUpperCase()} · {data.partCount} part{data.partCount === 1 ? '' : 's'}
            </dd>
            <dt>Priority</dt>
            <dd>{data.priority}</dd>
            {data.correlationId && (
              <>
                <dt>Correlation</dt>
                <dd className="mono">{data.correlationId}</dd>
              </>
            )}
            <dt>Created</dt>
            <dd>{fmtFull(data.createdAt)}</dd>
            <dt>Expires</dt>
            <dd>{fmtFull(data.expiresAt)}</dd>
            <dt>Trace</dt>
            <dd className="mono small">{data.traceId}</dd>
          </dl>

          {data.deliveries.map((d) => (
            <section key={d.id} className="card">
              <header className="card-head">
                <span className="row" style={{ gap: 10 }}>
                  <span className="mono">{d.msisdn}</span>
                  <StatusPill status={d.status} />
                </span>
                <span className="faint small">
                  {d.clientName ?? 'unrouted'} · {d.attemptCount} attempt{d.attemptCount === 1 ? '' : 's'}
                </span>
              </header>
              <div className="card-body">
                {d.verdict && (
                  <div className="banner info" style={{ marginBottom: 16 }}>
                    <div>
                      <strong>Verdict.</strong> {d.verdict}
                    </div>
                  </div>
                )}

                <div className="timeline">
                  <TimelineItem when={d.createdAt} title="Accepted" tone="ok" />
                  {d.attempts.map((a) => (
                    <TimelineItem
                      key={a.attemptNo}
                      when={a.startedAt}
                      title={`Attempt ${a.attemptNo} — ${a.clientName ?? 'no device'}`}
                      detail={
                        <>
                          <span className="faint">routed: {a.routingReason}</span>
                          {a.outcome && (
                            <>
                              {' · '}
                              <span>{a.outcome}</span>
                            </>
                          )}
                          {a.reasonCode && (
                            <div style={{ color: 'rgb(var(--danger))' }}>
                              {humanReason(a.reasonCode)}
                              {a.modemError ? ` — ${a.modemError}` : ''}
                            </div>
                          )}
                          {a.reasonDetail && <div>{a.reasonDetail}</div>}
                        </>
                      }
                      tone={a.outcome === 'Submitted' ? 'ok' : a.outcome ? 'bad' : undefined}
                    />
                  ))}
                  {d.submittedAt && <TimelineItem when={d.submittedAt} title="Submitted to the network" tone="ok" />}
                  {d.deliveredAt && <TimelineItem when={d.deliveredAt} title="Delivered to the handset" tone="ok" />}
                  {d.failedAt && (
                    <TimelineItem
                      when={d.failedAt}
                      title={`Failed — ${humanReason(d.reasonCode)}`}
                      detail={d.reasonDetail}
                      tone="bad"
                    />
                  )}
                </div>

                {d.parts.length > 1 && (
                  <div className="faint small" style={{ marginTop: 12 }}>
                    Parts:{' '}
                    {d.parts.map((p) => `${p.seq}/${d.parts.length} ${p.status.toLowerCase()}`).join(' · ')}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </Modal>
  )
}

function TimelineItem({
  when,
  title,
  detail,
  tone,
}: {
  when: string
  title: string
  detail?: React.ReactNode
  tone?: 'ok' | 'bad'
}) {
  return (
    <div className={`tl-item ${tone ?? ''}`}>
      <div className="tl-time">{fmtFull(when)}</div>
      <div className="tl-title">{title}</div>
      {detail && <div className="tl-detail">{detail}</div>}
    </div>
  )
}
